import { Injectable, computed, inject, signal } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { OrganizationDeviceService } from '../../Modules/Organizations/Services/organization-device.service';
import { ResourceStateService } from '../../Modules/Shared/Services/resource-state.service';
import { DEFAULT_PAGE_SIZE, FIRST_PAGE } from '../../Consts/backoffice.const';
import { MetricState } from '../../Modules/Shared/Types/metric-state.type';
import { Device } from '../../Models/backoffice/device.model';
import { DeviceFilters } from '../../Modules/Organizations/Interfaces/device-filters.interface';

import { LaravelDefaultResponse } from '../../Interfaces/api/shared/response.interface';
import { DeviceListResponse } from '../../Interfaces/api/backoffice/device.interface';

@Injectable()
export class OrganizationDeviceFacade {
  private readonly _deviceService = inject(OrganizationDeviceService);
  private readonly _resourceState = inject(ResourceStateService);

  private readonly _organizationId = signal<string>('');
  private readonly _page = signal<number>(FIRST_PAGE);
  private readonly _pageSize = signal<number>(DEFAULT_PAGE_SIZE);
  private readonly _filters = signal<DeviceFilters>({ search: '', state: '' });

  private readonly _devices = httpResource<LaravelDefaultResponse<DeviceListResponse>>(() =>
    this._organizationId()
      ? this._deviceService.listRequest(
          this._organizationId(),
          this._filters(),
          this._page(),
          this._pageSize(),
        )
      : undefined,
  );

  readonly filters = computed<DeviceFilters>(() => this._filters());

  readonly page = computed<number>(() => this._page());

  readonly pageSize = computed<number>(() => this._pageSize());

  readonly devices = computed<Device[]>(
    () => this._deviceService.toPagedResult(this._resourceState.valueOf(this._devices)).items,
  );

  readonly total = computed<number>(
    () => this._deviceService.toPagedResult(this._resourceState.valueOf(this._devices)).total,
  );

  readonly counts = computed<Record<string, number>>(() =>
    this._deviceService.toCounts(this._resourceState.valueOf(this._devices)),
  );

  readonly state = computed<MetricState>(() =>
    this._resourceState.metricState(this._devices.status()),
  );

  readonly isEmpty = computed<boolean>(() => this.state() === 'ready' && this.devices().length === 0);

  readonly hasActiveFilters = computed<boolean>(() =>
    Object.values(this._filters()).some((value) => value !== ''),
  );

  load(organizationId: string): void {
    this._organizationId.set(organizationId);
  }

  applyFilters(filters: DeviceFilters): void {
    this._filters.set(filters);
    this._page.set(FIRST_PAGE);
  }

  clearFilters(): void {
    this.applyFilters({ search: '', state: '' });
  }

  goToPage(page: number): void {
    this._page.set(Math.max(FIRST_PAGE, page));
  }

  changePageSize(pageSize: number): void {
    this._pageSize.set(pageSize);
    this._page.set(FIRST_PAGE);
  }

  reload(): void {
    this._devices.reload();
  }
}
